import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';
import { PostProjectTaskTag, PatchProjectTaskTag, DeleteProjectTaskTag, PatchTask } from '../../redux/taskSlice';
import { GetDataroomItemInfo, GetFolderDefaults } from '../../redux/dataroomSlice';
import { AddCircle24Filled, Checkmark16Regular, Dismiss16Regular, Edit16Regular, Delete16Regular } from '@fluentui/react-icons';
import { Menu, MenuTrigger, MenuPopover, MenuList, MenuDivider } from '@fluentui/react-components';
import DoubleConfirmationModal from '../CommonComponents/DoubleConfirmationModal/DoubleConfirmationModal';
import SimpleAlertModal from '../CommonComponents/DoubleConfirmationModal/SimpleAlertModal';
import styles from './css/index.module.css';

const TagManagementMenu = ({ onTagSelect, children, taskData, taskWorkstreamName, selectedTagName }) => {
    const dispatch = useDispatch();
    const { projectTags, projectWorkstreams, taskData: allProjectTasks } = useSelector(state => state.task);
    const { workstreams, default_folders, items } = useSelector(state => state.dataroom);
    const [newTagName, setNewTagName] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [open, setOpen] = useState(false);
    const [dataroomTags, setDataroomTags] = useState([]);
    const selectedItemRef = useRef(null);
    const [hoveredTagId, setHoveredTagId] = useState(null);
    const [editingTagId, setEditingTagId] = useState(null);
    const [editingTagName, setEditingTagName] = useState('');
    const [tagActionLoadingId, setTagActionLoadingId] = useState(null);
    const [tagPendingDelete, setTagPendingDelete] = useState(null);
    const [cannotDeleteTagName, setCannotDeleteTagName] = useState('');
    const deleteModalRef = useRef(null);
    const [manageModalOpen, setManageModalOpen] = useState(false);

    // When menu opens, scroll the selected tag into view
    useEffect(() => {
        if (!open || !selectedTagName) return;
        const timer = setTimeout(() => {
            selectedItemRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, 0);
        return () => clearTimeout(timer);
    }, [open, selectedTagName]);

    // Check if task workstream is from dataroom
    const isDataroomWorkstream = taskWorkstreamName && workstreams?.some(ws => ws.name === taskWorkstreamName);

    // Fetch dataroom tags when menu is opened and workstream is from dataroom
    const fetchDataroomData = () => {
        if (isDataroomWorkstream && taskWorkstreamName) {
            // Get the workstream object
            const selectedWorkstream = workstreams?.find(ws => ws.name === taskWorkstreamName) ||
                projectWorkstreams?.find(ws => ws.name === taskWorkstreamName) || { id: `custom_${taskWorkstreamName}`, name: taskWorkstreamName, source: 'custom' };

            if (selectedWorkstream?.id) {
                dispatch(GetDataroomItemInfo(selectedWorkstream.id));
                dispatch(GetFolderDefaults(selectedWorkstream.name));
            }
        }
    };

    // Handle menu open/close
    const handleMenuOpenChange = (e, data) => {
        setOpen(data.open);
        if (data.open) {
            // Only fetch data when menu is opened
            fetchDataroomData();
        }
    };

    // Update dataroom tags when default_folders or items change
    useEffect(() => {
        if (isDataroomWorkstream) {
            const combinedOptions = [...new Set([...default_folders.map(folder => folder?.ws_folder), ...items.map(item => item?.name)])].map(label => ({
                id: `dataroom_${label}`,
                name: label,
                source: 'dataroom',
            }));
            setDataroomTags(combinedOptions);
        } else {
            setDataroomTags([]);
        }
    }, [default_folders, items, isDataroomWorkstream]);

    // Get all tags from different sources (no duplicates)
    const getAllTags = () => {
        const tagMap = new Map();

        // 1. Dataroom tags (when workstream is from dataroom)
        if (isDataroomWorkstream && dataroomTags.length > 0) {
            dataroomTags.forEach(tag => {
                tagMap.set(tag.name, { ...tag, source: 'dataroom' });
            });
        }

        // 2. Project tags
        if (projectTags && projectTags.length > 0) {
            projectTags.forEach(tag => {
                tagMap.set(tag.name, { ...tag, source: 'project' });
            });
        }

        // 3. All tags from task data
        const getAllTagsFromTaskData = () => {
            // Handle both array and object structures
            let tasks = [];
            if (Array.isArray(taskData)) {
                tasks = taskData;
            } else if (taskData && taskData.data && Array.isArray(taskData.data)) {
                tasks = taskData.data;
            } else if (taskData && taskData.groups && Array.isArray(taskData.groups)) {
                // Handle grouped data - flatten all groups
                tasks = taskData.groups.flatMap(group => group.tasks || []);
            } else {
                return [];
            }

            const tagSet = new Set();
            tasks.forEach(task => {
                const taskTags = Array.isArray(task.tags) ? task.tags : task?.tag_repr ? [task.tag_repr] : [];
                taskTags.forEach(tag => {
                    if (tag && tag.trim() !== '') {
                        tagSet.add(tag);
                    }
                });
            });
            return Array.from(tagSet);
        };

        const taskTagNames = getAllTagsFromTaskData();
        taskTagNames.forEach(tagName => {
            if (!tagMap.has(tagName)) {
                tagMap.set(tagName, {
                    id: `task_${tagName}`,
                    name: tagName,
                    source: 'task',
                });
            }
        });

        return Array.from(tagMap.values());
    };

    const allTags = getAllTags();

    // Helper: check if a given tag name is currently used by any task
    const isTagInUse = tag => {
        if (!tag || !tag.name) return false;
        const normalizedTarget = tag.name.toString().trim().toLowerCase();
        if (!normalizedTarget) return false;

        const sourceTasks = Array.isArray(allProjectTasks) ? allProjectTasks : [];
        if (!sourceTasks.length) return false;

        return sourceTasks.some(task => {
            const rawTags = Array.isArray(task.tags) ? task.tags : task?.tag_repr ? [task.tag_repr] : [];
            return rawTags.some(t => {
                if (!t) return false;
                const normalized = t.toString().trim().toLowerCase();
                return normalized === normalizedTarget;
            });
        });
    };

    // Tags managed in the center-screen modal:
    // - All project task tags
    // - Plus any additional distinct tag names inferred from tasks (excluding dataroom-only tags)
    const managedTags = (() => {
        const list = [];
        const projectTagByName = new Map();

        (projectTags || []).forEach(tag => {
            if (!tag?.name) return;
            projectTagByName.set(tag.name, tag);
        });

        const inferredOnlyNames = new Set();
        allTags.forEach(tag => {
            if (!tag?.name) return;
            if (tag.source === 'dataroom') return;
            if (tag.source === 'task' && !projectTagByName.has(tag.name)) {
                inferredOnlyNames.add(tag.name);
            }
        });

        // Project tags first
        projectTagByName.forEach(tag => {
            list.push({
                id: tag.id,
                name: tag.name,
                isProjectTag: true,
            });
        });

        // Then task-only inferred tags
        inferredOnlyNames.forEach(name => {
            list.push({
                id: `task_only_${name}`,
                name,
                isProjectTag: false,
            });
        });

        // Sort alphabetically by name for a stable, UX-friendly list
        return list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    })();

    const handleAddTag = async () => {
        if (!newTagName.trim()) return;

        setIsAdding(true);
        try {
            await dispatch(PostProjectTaskTag({ name: newTagName.trim() }));
            setNewTagName('');
        } catch (error) {
            console.error('Error adding tag:', error);
        } finally {
            setIsAdding(false);
        }
    };

    const handleTagClick = tag => {
        onTagSelect(tag);
        setOpen(false);
    };

    const handleStartEditTag = tag => {
        if (!tag) return;
        setEditingTagId(tag.id);
        setEditingTagName(tag.name || '');
    };

    const handleCancelEditTag = () => {
        setEditingTagId(null);
        setEditingTagName('');
    };

    const handleSaveEditTag = async tag => {
        if (!tag) return;
        const trimmed = editingTagName.trim();
        if (!trimmed || trimmed === tag.name) {
            handleCancelEditTag();
            return;
        }

        const oldName = (tag.name || '').toString();
        const newName = trimmed;

        try {
            setTagActionLoadingId(tag.id);

            // 1) If this is a project tag, update the ProjectTaskTag record
            if (tag.isProjectTag) {
                await dispatch(
                    PatchProjectTaskTag({
                        id: tag.id,
                        body: { name: newName },
                    }),
                );
            }

            // 2) Update all tasks that currently use this tag name
            const sourceTasks = Array.isArray(allProjectTasks) ? allProjectTasks : [];
            const normalizedOld = oldName.toString().trim().toLowerCase();

            const patchPromises = [];

            sourceTasks.forEach(task => {
                const rawTags = Array.isArray(task.tags)
                    ? task.tags
                    : task?.tag_repr
                    ? [task.tag_repr]
                    : [];

                const hasTag = rawTags.some(t => {
                    if (!t) return false;
                    return t.toString().trim().toLowerCase() === normalizedOld;
                });

                if (!hasTag) return;

                const nextTags = [];
                rawTags.forEach(t => {
                    if (!t) return;
                    const isMatch = t.toString().trim().toLowerCase() === normalizedOld;
                    const candidate = isMatch ? newName : t;
                    if (!nextTags.includes(candidate)) {
                        nextTags.push(candidate);
                    }
                });

                patchPromises.push(
                    dispatch(
                        PatchTask({
                            body: { tags: nextTags },
                            id: task.id,
                            suppressToast: true,
                        }),
                    ),
                );
            });

            if (patchPromises.length > 0) {
                await Promise.all(patchPromises);
            }

            handleCancelEditTag();
        } catch (error) {
            console.error('Error updating tag:', error);
        } finally {
            setTagActionLoadingId(null);
        }
    };

    const handleDeleteTag = async tag => {
        if (!tag) return;

        // Prevent deleting tags that are still associated with tasks
        if (isTagInUse(tag)) {
            setCannotDeleteTagName(tag.name || '');
            return;
        }

        // Only project tags have backing records to delete
        if (!tag.isProjectTag) {
            return;
        }

        try {
            setTagActionLoadingId(tag.id);
            await dispatch(DeleteProjectTaskTag(tag.id));
            if (editingTagId === tag.id) {
                handleCancelEditTag();
            }
        } catch (error) {
            console.error('Error deleting tag:', error);
        } finally {
            setTagActionLoadingId(null);
        }
    };

    return (
        <>
            {/* Warning when a tag is still in use */}
            {cannotDeleteTagName && (
                <SimpleAlertModal
                    open={!!cannotDeleteTagName}
                    onClose={() => setCannotDeleteTagName('')}
                    message="This tag cannot be deleted because it is currently associated with one or more tasks."
                />
            )}
            {/* Global delete confirmation modal for project tags */}
            <DoubleConfirmationModal
                isMenuChild={true}
                message="Deleting a Tag permanently removes it from the Project, and this is irreversible."
                handleAction={() => {
                    if (tagPendingDelete) {
                        handleDeleteTag(tagPendingDelete);
                    }
                }}>
                <span ref={deleteModalRef} />
            </DoubleConfirmationModal>

            <Menu open={open} onOpenChange={handleMenuOpenChange}>
                <MenuTrigger>{children}</MenuTrigger>
                <MenuPopover style={{ width: '232px', maxHeight: '400px', overflowY: 'auto' }}>
                    <MenuList>
                        {/* All Tags in one list */}
                        {allTags.length > 0 ? (
                            allTags.map(tag => {
                                const isSelected =
                                    !!selectedTagName &&
                                    selectedTagName
                                        .split(',')
                                        .map(t => t.trim())
                                        .includes(tag.name);
                                const isHovered = hoveredTagId === tag.id;
                                const showCross = isSelected && isHovered;
                                const showCheck = isSelected && !isHovered;

                                return (
                                    <div
                                        key={tag.id}
                                        ref={isSelected ? selectedItemRef : undefined}
                                        onClick={() => handleTagClick(tag)}
                                        onMouseEnter={() => setHoveredTagId(tag.id)}
                                        onMouseLeave={() => setHoveredTagId(null)}
                                        className={styles.tagMenuItem}
                                        style={
                                            isSelected
                                                ? {
                                                      backgroundColor: 'rgba(142, 45, 226, 0.05)',
                                                  }
                                                : undefined
                                        }>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                width: '100%',
                                                gap: 8,
                                            }}>
                                            <span>{tag.name}</span>
                                            <>
                                                {showCheck && <Checkmark16Regular style={{ color: 'var(--Brand-Secondary)' }} />}
                                                {showCross && <Dismiss16Regular style={{ color: 'var(--Brand-Secondary)' }} />}
                                            </>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div>
                                <p>No tags available</p>
                            </div>
                        )}

                        {/* Manage Tags option (opens center-screen modal) */}
                        {allTags.length > 0 && (
                            <>
                                <MenuDivider />
                                <div
                                    onClick={() => {
                                        setManageModalOpen(true);
                                        setOpen(false);
                                        handleCancelEditTag();
                                    }}
                                    className={styles.tagMenuItem}>
                                    <span>Edit Tags</span>
                                </div>
                            </>
                        )}
                        {selectedTagName && (
                            <>
                                <MenuDivider />
                                <div
                                    onClick={() => {
                                        onTagSelect(null);
                                        setOpen(false);
                                    }}
                                    className={styles.tagMenuItem}>
                                    <span>Remove all tags</span>
                                </div>
                            </>
                        )}

                        {/* Add New Tag Section - Sticky at bottom */}
                        <div
                            style={{
                                position: 'sticky',
                                bottom: -5,
                                backgroundColor: 'white',
                                padding: '8px 12px',
                                marginTop: '8px',
                            }}>
                            <MenuDivider />
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                                <input
                                    type="text"
                                    placeholder="Add new tag"
                                    className={styles.tagInput}
                                    value={newTagName}
                                    style={{ width: '170px' }}
                                    onChange={e => setNewTagName(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddTag();
                                        }
                                    }}
                                />
                                <div>
                                    {' '}
                                    <AddCircle24Filled
                                        color="var(--Brand-Secondary)"
                                        onClick={handleAddTag}
                                        disabled={!newTagName.trim() || isAdding}
                                        style={{ cursor: 'pointer', fontSize: '20px' }}
                                    />
                                </div>
                            </div>
                        </div>
                    </MenuList>
                </MenuPopover>
            </Menu>

            {/* Centered Tag Management modal */}
            <Modal
                centered
                show={manageModalOpen}
                onHide={() => {
                    setManageModalOpen(false);
                    handleCancelEditTag();
                }}
                className={styles.modalMainBody}>
                <Modal.Body className={styles.confirmationModalBody}>
                    <div className={styles.confirmationModalMsg} style={{ width: '100%' }}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className={styles.tableFilterHeader + ' mb-0'}>Tags</h6>
                            <button
                                type="button"
                                onClick={() => {
                                    setManageModalOpen(false);
                                    handleCancelEditTag();
                                }}
                                className={styles.closeButton || 'btn btn-link p-0'}
                                style={{ border: 'none', background: 'transparent' }}>
                                <Dismiss16Regular color="var(--Brand-Primary)" />
                            </button>
                        </div>

                        {managedTags && managedTags.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {managedTags.map(tag => {
                                    const isEditingThis = editingTagId === tag.id;
                                    return (
                                        <div
                                            key={tag.id}
                                            className={styles.tagMenuItem}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 8,
                                            }}>
                                            {isEditingThis ? (
                                                <input
                                                    type="text"
                                                    className={styles.tagInput}
                                                    style={{ flex: 1 }}
                                                    value={editingTagName}
                                                    onChange={e => setEditingTagName(e.target.value)}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            handleSaveEditTag(tag);
                                                        } else if (e.key === 'Escape') {
                                                            e.preventDefault();
                                                            handleCancelEditTag();
                                                        }
                                                    }}
                                                    onBlur={() => handleSaveEditTag(tag)}
                                                    autoFocus
                                                />
                                            ) : (
                                                <span style={{ flex: 1 }}>{tag.name}</span>
                                            )}
                                            {!isEditingThis && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <Edit16Regular
                                                        style={{
                                                            color: 'var(--Neutral-Gray-6)',
                                                            cursor: tagActionLoadingId ? 'not-allowed' : 'pointer',
                                                            opacity: tagActionLoadingId === tag.id ? 0.5 : 1,
                                                        }}
                                                        onClick={() => {
                                                            if (tagActionLoadingId) return;
                                                            handleStartEditTag(tag);
                                                        }}
                                                    />
                                                    <Delete16Regular
                                                        style={{
                                                            color: 'var(--Neutral-Gray-6)',
                                                            cursor: tagActionLoadingId ? 'not-allowed' : 'pointer',
                                                            opacity: tagActionLoadingId === tag.id ? 0.5 : 1,
                                                        }}
                                                        onClick={() => {
                                                            if (tagActionLoadingId) return;
                                                            // If tag is in use, show warning immediately and skip double confirmation
                                                            if (isTagInUse(tag)) {
                                                                setCannotDeleteTagName(tag.name || '');
                                                                return;
                                                            }
                                                            setTagPendingDelete(tag);
                                                            deleteModalRef.current && deleteModalRef.current.click();
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="mb-0">No project tags available.</p>
                        )}
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default TagManagementMenu;
